import React, { useEffect } from "react";
import {
  NavLink,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  SIDEBAR_LINKS_SUPERADMIN,
  ROLE,
  getIcon,
  formatIntegrationName,
  CATEGORY_TABS,
} from "../constants/constants";
import cookie from "react-cookies";
import { Icons } from "../constants/icon-library";
import { clearUser } from "../features/user/userSlice";
import { upsertSetting } from "../features/settings/settingsSlice";
import {
  fetchIntegrationConversation,
  syncAllConversations,
} from "../features/integration-setting/integrationSettingSlice";

function SectionLabel({ label, collapsed }) {
  if (collapsed) return null;
  return (
    <div
      className="text-uppercase text-body-tertiary fw-bold px-3 mb-1"
      style={{ fontSize: 10, letterSpacing: "0.1em" }}
    >
      {label}
    </div>
  );
}

function NavItem({ to, icon, label, badge, end = false, active, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) => {
        const shouldBeActive = active !== undefined ? active : isActive;
        return `d-flex align-items-center gap-2 px-3 py-2 rounded-2 text-decoration-none mb-1 nav-sidebar-item ${
          shouldBeActive ? "nav-sidebar-item--active" : ""
        }`;
      }}
      style={
        collapsed
          ? { justifyContent: "center", paddingLeft: 0, paddingRight: 0 }
          : undefined
      }
    >
      <span
        className="flex-shrink-0"
        style={{ width: 18, textAlign: "center", position: "relative" }}
      >
        {icon}
        {collapsed && badge !== undefined && badge > 0 && (
          <span
            className="badge rounded-pill bg-primary"
            style={{
              position: "absolute",
              top: -6,
              right: -8,
              fontSize: 8,
              minWidth: 14,
              height: 14,
              padding: "0 3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="flex-grow-1 text-truncate" style={{ fontSize: 13 }}>
            {label}
          </span>
          {badge !== undefined && (
            <span
              className="badge rounded-pill flex-shrink-0"
              style={{ fontSize: 10 }}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function BottomButton({ icon, label, onClick, collapsed, disabled }) {
  return (
    <button
      className="d-flex align-items-center gap-2 px-3 py-2 rounded-2 w-100 border-0 bg-transparent text-start nav-sidebar-item mb-1"
      style={{
        fontSize: 13,
        justifyContent: collapsed ? "center" : "flex-start",
      }}
      onClick={onClick}
      disabled={disabled}
      title={collapsed ? label : undefined}
    >
      <span
        className="flex-shrink-0"
        style={{ width: 18, textAlign: "center" }}
      >
        {icon}
      </span>
      {!collapsed && <span>{label}</span>}
    </button>
  );
}

export default function Sidebar({ collapsed, onToggleCollapse }) {
  const { user } = useSelector((state) => state.user);
  const { conversationGroups, syncing } = useSelector(
    (state) => state.integrationSetting,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const activeGroup = searchParams.get("group");
  const activeCategory = searchParams.get("category");
  const isMyTasksActive = location.pathname === "/my-tasks";
  const isAllTasksActive = location.pathname === "/tasks" && !activeCategory;

  useEffect(() => {
    if (window.feather) window.feather.replace();
  }, []);

  useEffect(() => {
    if (user?.role && user.role !== ROLE.ADMIN) {
      dispatch(fetchIntegrationConversation());
    }
  }, [dispatch, user?.role]);

  const handleSync = () => {
    dispatch(syncAllConversations());
  };

  const handleLogout = () => {
    cookie.remove("token", { path: "/" });
    dispatch(clearUser());
    dispatch(upsertSetting());
    navigate("/sign-in", { replace: true });
  };

  const channelGroups = conversationGroups.filter((g) => g.key !== "all");

  if (!user || !user.role) return null;

  const isAdmin = user.role === ROLE.ADMIN;

  return (
    <nav
      className="navbar navbar-vertical navbar-expand-lg p-0"
      style={{
        width: collapsed ? 64 : undefined,
        minWidth: collapsed ? 64 : undefined,
        transition: "width .2s ease, min-width .2s ease",
      }}
    >
      <div
        className="collapse navbar-collapse"
        id="navbarVerticalCollapse"
        style={{ height: "100%" }}
      >
        <div
          className="navbar-vertical-content d-flex flex-column pb-0 px-2"
          style={{ height: "100%" }}
        >
          <div className="flex-grow-1 overflow-y-auto pt-2">
            {isAdmin ? (
              <>
                <SectionLabel label="Menu" collapsed={collapsed} />
                {SIDEBAR_LINKS_SUPERADMIN.map((link) => (
                  <NavItem
                    key={link.to}
                    to={link.to}
                    label={link.label}
                    collapsed={collapsed}
                    icon={
                      <i
                        className={`wb ${Icons(link.icon)} align-middle`}
                        style={{ fontSize: 14 }}
                      />
                    }
                  />
                ))}
              </>
            ) : (
              <>
                <SectionLabel label="Overview" collapsed={collapsed} />
                <NavItem
                  to="/dashboard"
                  end
                  label="Dashboard"
                  collapsed={collapsed}
                  icon={
                    <i
                      className="bi bi-speedometer2 align-middle"
                      style={{ fontSize: 14 }}
                    />
                  }
                />

                <SectionLabel label="Tasks" collapsed={collapsed} />

                {CATEGORY_TABS.map((category) => {
                  const isCategoryActive =
                    location.pathname === "/tasks" &&
                    activeCategory === category.key;
                  return (
                    <NavItem
                      key={category.key}
                      to={`/tasks?category=${category.key}`}
                      label={category.label}
                      active={isCategoryActive}
                      collapsed={collapsed}
                      icon={
                        <i
                          className={`wb ${category.icon || ""} align-middle ${
                            isCategoryActive ? "conv-icon-active" : ""
                          }`}
                          style={{ fontSize: 14 }}
                        />
                      }
                    />
                  );
                })}

                <NavItem
                  to="/tasks"
                  end
                  label="All Tasks"
                  active={isAllTasksActive}
                  collapsed={collapsed}
                  icon={
                    <i
                      className={`wb ${Icons("allTasks")} align-middle ${
                        isAllTasksActive ? "conv-icon-active" : ""
                      }`}
                      style={{ fontSize: 14 }}
                    />
                  }
                />
                <NavItem
                  to="/my-tasks"
                  label="My Tasks"
                  active={isMyTasksActive}
                  collapsed={collapsed}
                  icon={
                    <i
                      className={`wb ${Icons("myTasks")} align-middle ${
                        isMyTasksActive ? "conv-icon-active" : ""
                      }`}
                      style={{ fontSize: 14 }}
                    />
                  }
                />

                <SectionLabel label="Conversations" collapsed={collapsed} />

                {channelGroups.length === 0
                  ? !collapsed && (
                      <p
                        className="px-3 text-body-tertiary"
                        style={{ fontSize: 12 }}
                      >
                        No integration connected yet
                      </p>
                    )
                  : channelGroups.map((group) => {
                      const iconUrl = getIcon(group.platform);
                      return (
                        <NavItem
                          key={group.key}
                          to={`/conversations?group=${group.key}`}
                          label={formatIntegrationName(group.label)}
                          badge={
                            group.messages?.length || group.messageCount || 0
                          }
                          active={activeGroup === group.key}
                          collapsed={collapsed}
                          icon={
                            iconUrl ? (
                              <img
                                src={iconUrl}
                                alt={group.platform}
                                width={14}
                                height={14}
                                style={{ objectFit: "contain" }}
                              />
                            ) : (
                              <i
                                className={`bi ${
                                  group.isChannel ? "bi-hash" : "bi-grid"
                                } align-middle`}
                                style={{ fontSize: 14 }}
                              />
                            )
                          }
                        />
                      );
                    })}
              </>
            )}
          </div>

          <div className="flex-shrink-0 pb-3 pt-2 border-top">
            {!isAdmin && (
              <BottomButton
                label={syncing ? "Syncing..." : "Sync conversations"}
                onClick={handleSync}
                disabled={syncing}
                collapsed={collapsed}
                icon={
                  <i
                    className={`bi bi-arrow-repeat align-middle ${
                      syncing ? "spinner-border spinner-border-sm" : ""
                    }`}
                    style={{ fontSize: 14 }}
                  />
                }
              />
            )}

            <button
              type="button"
              className="d-flex align-items-center gap-2 px-3 py-2 rounded-2 w-100 border-0 bg-transparent text-start nav-sidebar-item mb-1"
              style={{
                fontSize: 13,
                justifyContent: collapsed ? "center" : "flex-start",
              }}
              onClick={onToggleCollapse}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <span
                className="flex-shrink-0"
                style={{ width: 18, textAlign: "center" }}
              >
                <i
                  className={`bi ${collapsed ? "bi-chevron-double-right" : "bi-chevron-double-left"}`}
                  style={{ fontSize: 14 }}
                />
              </span>
              {!collapsed && <span>Collapse</span>}
            </button>

            <BottomButton
              label="Sign out"
              onClick={handleLogout}
              collapsed={collapsed}
              icon={
                <i
                  className={`wb ${Icons("logoutUser")} align-middle`}
                  style={{ fontSize: 14 }}
                />
              }
            />
          </div>
        </div>
      </div>
    </nav>
  );
}
